// src/AssetManager/AufRestoreSuccessDialog.jsx
import * as React from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  Stack,
  Divider,
  Chip,
  Box,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useParams } from "react-router-dom";

import { useAuth } from "../contexts/AuthContext";
import { useFormContext } from "../contexts/FormContext";
import { safeGetItem, safeRemoveItem } from "../utils/storageUtils";

/**
 * AufRestoreSuccessDialog
 * ---------------------------------------------------------------------------
 * Shows after a field is restored into the AUF builder session.
 *
 * UX goals:
 * - confirm success
 * - clearly explain that PUBLISH is required for persistence / uploader availability
 * - clarify that option IDs were restored but not every field setting snapshot
 *
 * IMPORTANT (why this file now "hosts" itself):
 * - The restore flow queues a payload in storage under an org-scoped key
 * - This component reads the queued payload, opens itself, then consumes the key
 * - This removes the route timing issue (no need to render it on a specific page)
 *
 * Storage contract (written by FormLayoutContext):
 *   key: org_${orgId}_aufRestoreSuccess
 *   value: {
 *     orgId,
 *     aufId,
 *     label,
 *     type,
 *     restoredOptionIds,
 *     restoredAllSettings,
 *     createdAt,
 *   }
 *
 * Props:
 * - enabled: boolean (optional; default true). If false, the dialog will not read storage.
 * - layoutPath: string (optional). If provided, "Go to layout" will navigate there.
 *   Use "{formId}" token if you want: "/form-builder/{formId}/layout"
 *
 * NOTE:
 * - We intentionally do NOT expose a "Publish now" CTA here.
 *   The user should review the restored field in layout/editor first.
 */
const AUF_RESTORE_SUCCESS_KEY = (orgId) =>
  orgId ? `org_${orgId}_aufRestoreSuccess` : null;

export default function AufRestoreSuccessDialog({
  enabled = true,
  layoutPath = "/form-builder/{formId}/layout",
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { formId: routeFormId } = useParams();

  const { organizationId } = useAuth() || {};
  const { formId: ctxFormId, formIdReady } = useFormContext();

  const activeFormId = routeFormId || ctxFormId || null;
  const successKey = React.useMemo(
    () => AUF_RESTORE_SUCCESS_KEY(organizationId),
    [organizationId],
  );

  const [open, setOpen] = React.useState(false);
  const [payload, setPayload] = React.useState(null);

  // Derivations for rendering (fallbacks preserved)
  const label = payload?.label || "This field";
  const type = payload?.type || null;

  const restoredOptionIds =
    payload?.restoredOptionIds === true || payload?.restoredOptionIds === "true";
  const restoredAllSettings =
    payload?.restoredAllSettings === true ||
    payload?.restoredAllSettings === "true";

  // ─────────────────────────────────────────────────────────────
  // Self-hosting behavior: read queued payload from storage once
  // and consume it so it does not re-show on refresh/back/forward.
  // ─────────────────────────────────────────────────────────────
  React.useEffect(() => {
    if (!enabled) return;
    if (!successKey) return;
    if (!formIdReady) return;
    if (!activeFormId) return;

    const queued = safeGetItem(successKey);

    if (!queued || typeof queued !== "object") {
      // Nothing queued → nothing to show.
      return;
    }

    // Guardrail: only show for the form we’re currently viewing.
    // Prevents bleed if user has multiple tabs or navigates between forms.
    if (queued?.aufId && String(queued.aufId) !== String(activeFormId)) {
      console.warn("[AufRestoreSuccessDialog] Ignoring queued payload for different aufId:", {
        queuedAufId: queued?.aufId,
        activeFormId,
        successKey,
      });
      return;
    }

    console.log("[AufRestoreSuccessDialog] Showing AUF restore success dialog:", {
      activeFormId,
      successKey,
      label: queued?.label || null,
      type: queued?.type || null,
    });

    setPayload(queued);
    setOpen(true);

    // Consume immediately so it doesn’t re-appear on refresh.
    safeRemoveItem(successKey);
  }, [enabled, successKey, formIdReady, activeFormId]);

  const handleClose = React.useCallback(() => {
    setOpen(false);
    setPayload(null);
  }, []);

  const handleGoToLayout = React.useCallback(() => {
    if (!activeFormId) return;

    // layoutPath supports a "{formId}" token.
    const resolved =
      typeof layoutPath === "string"
        ? layoutPath.replace("{formId}", String(activeFormId))
        : `/form-builder/${activeFormId}/layout`;

    console.log("[AufRestoreSuccessDialog] Navigating to layout editor for restored field:", {
      activeFormId,
      to: resolved,
    });

    navigate(resolved, { state: { fromAufRestore: true } });
    setOpen(false);
  }, [navigate, activeFormId, layoutPath]);

  // If there is no payload, do not render the Dialog at all.
  // (Keeps DOM clean and prevents accidental open state)
  if (!open || !payload) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={fullScreen}
      maxWidth="sm"
      fullWidth
      aria-labelledby="auf-restore-success-title"
      aria-describedby="auf-restore-success-description"
    >
      <DialogTitle id="auf-restore-success-title" sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700} noWrap>
              Field restored
            </Typography>
          </Box>

          {/* “status” chip keeps this enterprise-y and scannable */}
          <Chip
            size="small"
            label="Pending publish"
            variant="outlined"
            sx={{ fontSize: 12 }}
          />
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="success" variant="outlined">
            <Typography variant="body2">
              <Box component="span" sx={{ fontWeight: 700 }}>
                {label}
              </Box>{" "}
              has been restored in your current Asset Upload Form editor session.
            </Typography>
          </Alert>

          <Alert severity="warning" variant="outlined">
            <Typography variant="body2" id="auf-restore-success-description">
              <Box component="span" sx={{ fontWeight: 700 }}>
                Publish required:
              </Box>{" "}
              To make this restored field available to your team (and to the
              uploader) and to persist it across sessions, you must publish the
              Asset Upload Form.
            </Typography>
          </Alert>

          <Divider />

          <Typography variant="subtitle2" fontWeight={700}>
            What was restored
          </Typography>

          <Typography variant="body2" sx={{ pl: 1 }}>
            • Field identity (UFI) to preserve consistency in reporting/mapping
          </Typography>

          <Typography variant="body2" sx={{ pl: 1 }}>
            • Dropdown option IDs{" "}
            <Box component="span" sx={{ fontWeight: 700 }}>
              {restoredOptionIds ? "(restored)" : "(not restored)"}
            </Box>
          </Typography>

          {!!type && (
            <Typography variant="body2" sx={{ pl: 1 }}>
              • Field type:{" "}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {String(type)}
              </Box>
            </Typography>
          )}

          <Divider />

          <Typography variant="subtitle2" fontWeight={700}>
            What may need review
          </Typography>

          <Typography variant="body2" sx={{ pl: 1 }}>
            • Some field-level settings (required, helper text, validations,
            display/mapping toggles, etc.){" "}
            <Box component="span" sx={{ fontWeight: 700 }}>
              {restoredAllSettings
                ? "were restored"
                : "may need to be reconfigured"}
            </Box>
            .
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Tip: After publishing, refresh Asset Studio to confirm the restored
            field is active and any “Archived fields” entries update accordingly.
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={handleClose} variant="text">
          Keep editing
        </Button>

        <Button onClick={handleGoToLayout} variant="contained">
          Go to layout
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AufRestoreSuccessDialog.propTypes = {
  enabled: PropTypes.bool,
  layoutPath: PropTypes.string,
};
